import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { ContentContainer, RectangleButton } from 'components';
import EditProfileDrawer from 'containers/EditProfileDrawer';
import { ThemeContext } from 'theme';
import { style } from 'utils/constants/style';

import fetchApi from 'hooks/fetchApi';
import API_NAME from 'services/api';

import Profile from 'containers/Profile';

import { postBlockUser } from 'services/blockUser';
import { PrivateContentPlaceholder } from 'components/PrivateContentPlaceholder/PrivateContentPlaceholder';
import ProfileDetailsCard from 'components/ProfileDetailsCard';
import GarageSlider from 'containers/GarageSlider';
import ProfileDetailsDescription from 'components/ProfileDetailsDescription';
import Link from 'next/link';
import Slider from 'components/Slider';
import RoutesSlider from 'containers/RoutesSlider';
import { GetSettings } from 'services';
import { getMyRoutesStart } from 'store/user/actions';

import fetchData from 'utils/fetchData';
import {
  UserDetailsWrap,
  UserDetailsDescriptionWrap,
  HeaderBlock,
  MyGarageBlock,
  HeaderBlockGarage,
  UnfollowPopup,
  UnfollowPopupWrap,
  UnfollowButtonWrap,
  ImageWrap,
  CancelButton,
  FollowUserName,
  FollowFullname,
  RouteDetails,
  RouteHeader,
  RouteSlider,
  SuggestedRoutesSliderItem,
} from './user_details.style';

export interface IUserDetails {
  data: {
    brands: [{ name: string; brand: string; _id: string }];
    business: {
      phone: string;
      isActive: boolean;
      interests: [{ _id: string; name: string }];
      email: string;
    };
    collaborators: any[];
    coverPhoto: string;
    description: string;
    followersCount: number;
    followingCount: number;
    followingReq: boolean;
    followingStatus: number;
    fullName: string;
    garage: { vehicles: string[] };
    geoLocation: { type: string; coordinates: number[] };
    groups: [
      {
        coverPhoto: string;
        isAdmin: boolean;
        isAdminObject: boolean;
        isMember: boolean;
        isMemberObject: boolean;
        name: string;
        profilePhoto: string;
        username: string;
        _id: string;
      }
    ];
    interests: [{ _id: string; name: string }];
    isAmbassador: boolean;
    isBusiness: boolean;
    isDonor: boolean;
    isMe: boolean;
    isNearby: boolean;
    isVerified: boolean;
    location: { address: string; lng: string; lat: string };
    postsCount: number;
    privacy: number;
    profilePhoto: string;
    uid: string;
    username: string;
    website: string;
    phone?: string;
    email?: string;
    _id: string;
  };
}

export interface IUnfolowPopUp {
  text?: string;
  profilePhoto?: string;
  username?: string;
  fullName?: string;
  Id: string;
  unfollowCallApiCallback?: () => void;
  loadingButtonHandler?: (loading: boolean) => void;
  actionModalPopupShow: (isShow: boolean) => void;
  cancelClick: () => void;
}

export const ActionModal = ({
  text,
  profilePhoto,
  username,
  fullName,
  Id,
  unfollowCallApiCallback,
  loadingButtonHandler,
  actionModalPopupShow,
  cancelClick,
}: IUnfolowPopUp) => {
  const { t } = useTranslation();

  return (
    <div>
      <UnfollowPopupWrap>
        <UnfollowPopup>
          <div
            style={{
              paddingBottom: '10px',
              paddingTop: '10px',
              fontWeight: 600,
            }}
          >
            {text}
          </div>
          <ImageWrap>
            {profilePhoto ? (
              <Image
                loader={() => profilePhoto}
                width={50}
                height={50}
                src={profilePhoto}
                alt="user photo"
                unoptimized
                priority
              />
            ) : (
              <Image
                width={50}
                height={50}
                src="/assets/img/emptyUserPhoto.png"
                alt="user photo"
                unoptimized
                priority
              />
            )}
          </ImageWrap>

          <FollowUserName>{username}</FollowUserName>
          <FollowFullname style={{ paddingBottom: '10px' }}>
            {fullName}
          </FollowFullname>
          <UnfollowButtonWrap>
            <RectangleButton
              color="white"
              loading={false}
              text={t('group:unfollowUppercase')}
              onClick={unfollowCallApiCallback}
              backgroundColor={style.mainYellow.yellow}
            />
          </UnfollowButtonWrap>
          <CancelButton onClick={() => cancelClick()}>
            {t('myGroups:cancel')}
          </CancelButton>
        </UnfollowPopup>
      </UnfollowPopupWrap>
    </div>
  );
};

const UserDetails = ({
  userId,
  staticData,
}: {
  userId: string;
  staticData: any;
}) => {
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [unfollowPopupShow, setUnfollowPopupShow] = useState(false);
  const [data, setData] = useState(staticData?.data || {});
  const userDetails = useSelector((store) => store.user?.userDetails);
  
  const [followingStatus, setFollowingStatus] = useState(
    staticData?.data?.followingStatus
  );
  const { myRoutes, getMyEventsReqStat, isRedirectFromMyEvents } = useSelector(
    (state) => state.user
  );
  const dispatch = useDispatch();

  const [units, setUnits] = useState<Boolean>();

  useEffect(() => {
    GetSettings(userDetails?._id).then((res) => {
      setUnits(res?.units);
    });
  }, [userDetails]);

  useEffect(() => {
    dispatch(getMyRoutesStart(userId));
  }, [userDetails]);

  const context: any = useContext(ThemeContext);

  const garageData = useSelector((store) => store.fetchData);

  const { action, loading } = fetchApi();

  const { t } = useTranslation();

  const getUserGarage = useCallback(() => {
    action(API_NAME.USER_GET_GARAGE, {
      params: {
        userId,
        offset: 0,
        limit: 20,
      },
    });
  }, []);

  useEffect(() => {
    getUserGarage();
  }, []);

  const onUserEditClickOrFollowClick = () => {
    if (!data.isMe) {
      setLoadingButton(true);
    }

    if (data.isMe) {
      setIsOpenEditForm(true);
    }
    if (data._id && !data.isMe && followingStatus === 0) {
      action(API_NAME.PROFILE_USER_FOLLOW, {
        params: {
          myId: context?.userId,
          userId: data?._id,
        },
      });
      if (data?.privacy === 1) {
        setFollowingStatus(1);
      } else {
        setFollowingStatus(2);
      }
    }
    if (data._id && !data.isMe && followingStatus === 2) {
      setUnfollowPopupShow(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const onBlockUser = (id) => {
    if (!data.isMe) {
      postBlockUser(id);
    }
  };

  const onCancelClick = () => {
    setUnfollowPopupShow(false);
    setLoadingButton(false);
    document.body.style.overflow = 'scroll';
  };

  const showPrivateContent = !data?.isMe
    ? data?.privacy === 1
      ? data?.followingStatus === 2
      : true
    : true;

  const renderUserDetailInfo = () => {
    const {
      description,
      website,
      business,
      phone,
      email,
      interests,
      brands,
      location: userLocation,
    } = data || {};

    if (
      description?.trim() ||
      website?.trim() ||
      business?.phone?.trim() ||
      business?.email?.trim() ||
      phone?.trim() ||
      email?.trim() ||
      interests?.length ||
      brands?.length ||
      userLocation?.address?.trim()
    ) {
      return (
        <>
          <HeaderBlock>{t('profile:profileDetails')}</HeaderBlock>
          <UserDetailsDescriptionWrap>
            {/* <DetailsDescription data={data} type="profile" /> */}
            <ProfileDetailsDescription data={data} />
          </UserDetailsDescriptionWrap>
        </>
      );
    }

    return null;
  };

  const unfollowUser = () => {
    action(API_NAME.PROFILE_USER_UNFOLLOW, {
      params: {
        myId: context?.userId,
        userId: data?._id,
      },
    });
    setFollowingStatus(0);
    setUnfollowPopupShow(false);
  };

  return (
    <>
      {unfollowPopupShow && (
        <ActionModal
          text={t('profile:areYouSureYouWantToUnfollow')}
          actionModalPopupShow={setUnfollowPopupShow}
          cancelClick={onCancelClick}
          Id={data?._id}
          unfollowCallApiCallback={unfollowUser}
          loadingButtonHandler={setLoadingButton}
          username={data?.username}
          fullName={data?.fullName}
          profilePhoto={data?.profilePhoto.trim()}
        />
      )}

      <ContentContainer>
        <UserDetailsWrap>
          {/* {userData && (
              <DetailsMainCard
                data={userData}
                onClick={onUserEditClickOrFollowClick}
                loadingButton={loadingButton}
                userPosts={userPosts}
                onBlockUser={(id) => onBlockUser(id)}
              />
            )} */}
          {data && (
            <ProfileDetailsCard
              id={data?._id}
              coverPhoto={data?.coverPhoto}
              profilePhoto={data?.profilePhoto}
              isNearby={data?.isNearby}
              isMe={data?.isMe}
              username={data?.username}
              isVerified={data?.isVerified}
              isAmbassador={data?.isAmbassador}
              isDonor={data?.isDonor}
              fullName={data?.fullName}
              followersCount={data?.followersCount}
              followingCount={data?.followingCount}
              followingStatus={followingStatus}
              postsCount={data?.postsCount}
              onBlockUser={(id) => onBlockUser(id)}
              onEditProfile={() => setIsOpenEditForm(true)}
              onClick={onUserEditClickOrFollowClick}
            />
          )}
          <>
            {showPrivateContent ? (
              <>
                {/* {userData.isMe ? (
                  <MyGarageBlock isMe={userData.isMe}>
                    <HeaderBlockGarage isMe={userData.isMe}>
                      {t('menu:garageItem')}
                    </HeaderBlockGarage>
                    <GarageComponent
                      vehicle={userGarage}
                      isMe={userData.isMe}
                    />
                  </MyGarageBlock>
                ) : (
                  <>
                    {userGarage.length > 0 && (
                      <MyGarageBlock>
                        <HeaderBlockGarage isMe={userData.isMe}>
                          {`${userData.username}  ${t('common:garage')}`}
                        </HeaderBlockGarage>
                        <GarageComponent
                          vehicle={userGarage}
                          isMe={userData.isMe}
                        />
                      </MyGarageBlock>
                    )}
                  </>
                )} */}

                <MyGarageBlock isMe={data?.isMe}>
                  <HeaderBlockGarage type="h1" isMe={data?.isMe}>
                    {data?.isMe
                      ? t('menu:garageItem')
                      : `${data?.username}  ${t('common:garage')}`}
                  </HeaderBlockGarage>
                  <GarageSlider
                    vehicles={garageData[API_NAME.USER_GET_GARAGE]?.data}
                    isMe={data?.isMe}
                  />
                </MyGarageBlock>
                {(myRoutes?.length && (
                  <RouteDetails>
                    <RouteHeader>
                      <HeaderBlockGarage type="h1" isMe={data?.isMe}>
                        {`${data?.username}'s routes (${myRoutes?.length})`}
                      </HeaderBlockGarage>
                      <Link href="/routes">{t('common:viewAll')}</Link>
                    </RouteHeader>
                    <RouteSlider>
                      <Slider
                        superLargeDesktop={2}
                        desktop={2}
                        tablet={2}
                        mobile={1}
                      >
                        {myRoutes?.map((item: any) => (
                          <SuggestedRoutesSliderItem key={item?._id}>
                            <RoutesSlider
                              id={item?._id}
                              previewImageUrl={item?.previewImageUrl}
                              averageRating={item?.averageRating}
                              time={item?.time}
                              name={item?.name}
                              distance={item?.distance}
                              units={units}
                              isAuthenticate={context?.isAuthenticate}
                            />
                          </SuggestedRoutesSliderItem>
                        ))}
                      </Slider>
                    </RouteSlider>
                  </RouteDetails>
                )) ||
                  null}
                {renderUserDetailInfo()}
              </>
            ) : (
              <PrivateContentPlaceholder type="user" />
            )}
          </>
        </UserDetailsWrap>
      </ContentContainer>
      {/*
      {userPosts?.length > 0 && showPrivateContent && (
        <ContentContainerPosts>
          <HeaderBlock>{t('feed:posts')}</HeaderBlock>

          {userPosts?.length > 0 &&
            userPosts.map((el, i) => (
              <UserPostWrap key={`changeFeedType-${i}`}>
                <PostComponent
                  data={el}
                  isLiked={likedPostIds.includes(el._id)}
                  isClickable={![1, 3, 4].includes(el.type)}
                />
              </UserPostWrap>
            ))}
        </ContentContainerPosts>
      )} */}

      {showPrivateContent && <Profile id={data?._id} />}
      {data?.isMe && (
        <EditProfileDrawer
          initialValues={data}
          open={isOpenEditForm}
          onClose={() => setIsOpenEditForm(false)}
          onUdate={setData}
        />
      )}
    </>
  );
};

export default UserDetails;

export async function getServerSideProps({ query, req, res }) {
  let tsec = req?.cookies?.tsec;

  if (!tsec) {
    const loginAnonymousResp = await fetchData(
      `${process.env._DB_HOST}api/v11/auth/${process.env._ANONYMOUS_USERNAME}`,
      {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: process.env._ANONYMOUS_PASSWORD }),
        isAuthenticated: false,
      }
    ).then((resp) => resp.json());

    if (loginAnonymousResp.data.ok) {
      tsec = loginAnonymousResp?.data?.token;
    }
  }

  const response = await fetchData(
    `${process.env._DB_HOSTv12}api/v12/user/${query?.id}`,
    {
      method: 'GET',
      headers: { tsec, clubId: req?.cookies?.clubId },
    }
  );
  const data = await response.json();

  return {
    props: {
      staticData: data,
      userId: query.id,
    },
  };
}
